import { atom, computed } from 'nanostores';
import { toast } from 'react-toastify';
import { upgradePricingAction } from '~/lib/stores/pricing/helpers';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import type {
  PricingCardAction,
  PricingCardDropdownOption,
  PricingCardMessage,
  PricingMembershipState,
} from '~/lib/stores/pricing/types';
import { createProCheckoutSession } from '~/lib/subscription/subscriptionApi';
import { personalPricingList, type Pricing } from '~/utils/pricing';

class FreePricingSettingsStore {
  membership = atom<PricingMembershipState | null>(null);

  actions = atom<PricingCardAction[]>([]);

  messages = atom<PricingCardMessage[]>([]);

  selectedPricing = atom<Pricing>(personalPricingList[0]);

  activePricing = atom<Pricing | null>(null);

  nextHigherTierPricing = computed([this.activePricing], (activePricing) => {
    if (!activePricing) {
      return null;
    }

    const nextTier = personalPricingList.find((pricing) => pricing.tier === activePricing.tier + 1);

    return nextTier ?? null;
  });

  dropdownOptions = computed([this.membership], (membership) => {
    const pricingOptions: PricingCardDropdownOption[] = [];

    personalPricingList.forEach((pricing) => {
      const isActive = pricing.tier === membership?.tier;
      const isScheduled = pricing.tier === membership?.nextTier;

      pricingOptions.push({
        pricing,
        badge: isActive ? 'current' : isScheduled ? 'scheduled' : undefined,
      });
    });

    return pricingOptions;
  });

  isLoading = atom(false);

  selectPricing(pricing: Pricing) {
    this.selectedPricing.set(pricing);
  }

  init() {
    this.messages.set([
      {
        text: 'Your current plan: Free',
        placement: 'under-action',
      },
    ]);

    this.actions.set([
      upgradePricingAction({
        plan: 'pro',
        hasCanceledSubscription: false,
        onUpgrade: () => {
          const selectedPricing = this.selectedPricing.get();

          this.upgrade(selectedPricing);
        },
      }),
    ]);
  }

  async upgrade(pricing: Pricing) {
    this.isLoading.set(true);

    const interval = pricingStore.selectedInterval.get();

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const freeTrialParam = urlParams.get('free_trial');

      const sessionUrl = await createProCheckoutSession(pricing.level, interval, freeTrialParam || undefined);

      window.open(sessionUrl, '_self');
    } catch {
      toast.error(`Failed to initiate checkout. Please try again later.`);
    } finally {
      this.isLoading.set(false);
    }
  }
}

export const freePricingSettingsStore = new FreePricingSettingsStore();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { freePricingSettingsStore });
}
